import React from 'react';
import { marriageBanns as api } from '../../api/endpoints';
import { formatDate } from '../../utils/format';
import { FormLayout, Input, Select, Badge } from '../../components/ui';
import type { RegisterConfig } from './registerConfig';
import RegisterMemberField from './MemberField';
import type { MarriageBann, Member } from '../../types';

const APPROVAL_STATUSES = ['Pending', 'Approved', 'Objected', 'Cancelled'];

const EMPTY = {
  bannNo: '', groomChurch: '', groomId: '', groomName: '',
  brideChurch: '', brideId: '', brideName: '',
  firstDate: '', secondDate: '', thirdDate: '',
  officiatingPresbyter: '', objections: '', approvalStatus: 'Pending',
  /** Form-local: the picked couple, stripped before saving. */
  _groom: null as Member | null,
  _bride: null as Member | null
};

type Form = typeof EMPTY;

/** How many of the three publications have been made. */
const published = (r: { firstDate?: string; secondDate?: string; thirdDate?: string }) =>
  [r.firstDate, r.secondDate, r.thirdDate].filter(Boolean).length;

const tone = (status?: string) =>
  status === 'Approved' ? 'success'
  : status === 'Objected' || status === 'Cancelled' ? 'error'
  : 'neutral';

const Document = (r: MarriageBann) => (
  <div>
    <p>
      Notice is hereby given of the intended marriage between{' '}
      <strong>{r.groomName || r.groomId || '—'}</strong> of <strong>{r.groomChurch || '—'}</strong>{' '}
      and <strong>{r.brideName || r.brideId || '—'}</strong> of <strong>{r.brideChurch || '—'}</strong>.
    </p>

    {/* Unstyled on purpose — the chosen template rules the table. */}
    <table style={{ marginTop: '20px' }}>
      <tbody>
        {[
          ['First Publication', r.firstDate ? formatDate(r.firstDate) : 'Not yet published'],
          ['Second Publication', r.secondDate ? formatDate(r.secondDate) : 'Not yet published'],
          ['Third Publication', r.thirdDate ? formatDate(r.thirdDate) : 'Not yet published'],
          ['Objections', r.objections || 'None recorded'],
          ['Status', r.approvalStatus || 'Pending']
        ].map(([label, value]) => (
          <tr key={label}>
            <th style={{ width: '40%' }}>{label}</th>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '70px' }}>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>
        {r.officiatingPresbyter || 'Officiating Presbyter'}
      </div>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Church Seal</div>
    </div>
  </div>
);

const config: RegisterConfig<MarriageBann, Form> = {
  icon: '📜',
  title: 'Marriage Banns Register',
  prefix: 'BAN',
  label: 'Banns Entry',
  resource: api,
  emptyForm: EMPTY,
  toForm: (r) => ({
    ...EMPTY,
    bannNo: r.bannNo || '', groomChurch: r.groomChurch || '', groomId: r.groomId || '',
    groomName: r.groomName || '', brideName: r.brideName || '',
    brideChurch: r.brideChurch || '', brideId: r.brideId || '',
    firstDate: r.firstDate || '', secondDate: r.secondDate || '', thirdDate: r.thirdDate || '',
    officiatingPresbyter: r.officiatingPresbyter || '', objections: r.objections || '',
    approvalStatus: r.approvalStatus || 'Pending'
  }),
  validate: (f) => {
    // Names, not member ids: banns are commonly published for a couple where
    // only one side belongs to this parish.
    if (!f.groomName.trim() || !f.brideName.trim()) return 'Both the groom and bride names are required.';
    if (f.groomId && f.groomId === f.brideId) return 'The groom and bride cannot be the same member.';
    // Banns are read on three successive Sundays; approval before the third
    // publication would put the marriage ahead of its own notice period.
    if (f.approvalStatus === 'Approved' && published(f) < 3) {
      return 'All three publication dates are required before the banns can be Approved.';
    }
    const dates = [f.firstDate, f.secondDate, f.thirdDate].filter(Boolean);
    if (dates.some((d, i) => i > 0 && d < dates[i - 1])) {
      return 'The publication dates must run in order.';
    }
    return null;
  },
  rowDate: (r) => r.firstDate,
  searchKeys: ['recordId', 'bannNo', 'groomName', 'brideName', 'groomId', 'brideId', 'groomChurch', 'brideChurch', 'officiatingPresbyter'],
  searchPlaceholder: 'Search by Bann No, Groom or Bride...',
  columns: [
    {
      key: 'recordId',
      header: 'Bann No.',
      nowrap: true,
      render: (r: MarriageBann) => <strong>{r.bannNo || r.recordId}</strong>
    },
    {
      key: 'groomName',
      header: 'Groom',
      render: (r: MarriageBann) => (
        <>
          <strong>{r.groomName || r.groomId || '-'}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.groomChurch || '-'}</div>
        </>
      )
    },
    {
      key: 'brideName',
      header: 'Bride',
      render: (r: MarriageBann) => (
        <>
          <strong>{r.brideName || r.brideId || '-'}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.brideChurch || '-'}</div>
        </>
      )
    },
    {
      key: 'publications',
      header: 'Publications',
      align: 'center',
      render: (r: MarriageBann) => {
        const done = published(r);
        return <Badge tone={done === 3 ? 'success' : done === 0 ? 'neutral' : 'warning'}>{done} / 3</Badge>;
      }
    },
    {
      key: 'approvalStatus',
      header: 'Status',
      render: (r: MarriageBann) => <Badge tone={tone(r.approvalStatus)}>{r.approvalStatus || 'Pending'}</Badge>
    },
    { key: 'objections', header: 'Objections', render: (r: MarriageBann) => r.objections || 'None' }
  ],
  printModule: 'Marriage',
  printDocumentType: 'BANNS_NOTICE',
  listDocumentType: 'BANNS_REGISTER',
  printData: (r: any) => ({ ...r, marriageDate: formatDate(r.marriageDate || r.date) }),
  renderDocument: Document,
  fields: (form, setForm, ctx) => (
    <>
      <FormLayout.Grid>
        <Input
          label="Bann Number" placeholder="e.g. BN-2026-003"
          value={form.bannNo} onChange={(e) => setForm({ ...form, bannNo: e.target.value })}
        />
        <Select
          label="Approval Status" placeholder="" options={APPROVAL_STATUSES}
          value={form.approvalStatus} onChange={(e) => setForm({ ...form, approvalStatus: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Couple" />
      <FormLayout.Grid>
        {/* Banns are often published where only one side is on these rolls, so
            the picker fills the name in and the name is what is required. */}
        <RegisterMemberField
          form={form}
          setForm={setForm}
          idKey="groomId"
          memberKey="_groom"
          label="Groom (member)"
          hint="Leave blank if the groom belongs to another church."
          filter={(m) => m.gender !== 'Female'}
          isDarkMode={ctx.isDarkMode}
          autofill={(member) => ({ groomName: member?.name || '' })}
        />
        <RegisterMemberField
          form={form}
          setForm={setForm}
          idKey="brideId"
          memberKey="_bride"
          label="Bride (member)"
          hint="Leave blank if the bride belongs to another church."
          filter={(m) => m.gender !== 'Male'}
          isDarkMode={ctx.isDarkMode}
          autofill={(member) => ({ brideName: member?.name || '' })}
        />
        <Input
          label="Groom Name" required placeholder="e.g. Samuel David"
          value={form.groomName} onChange={(e) => setForm({ ...form, groomName: e.target.value })}
        />
        <Input
          label="Bride Name" required placeholder="e.g. Anna Rachel"
          value={form.brideName} onChange={(e) => setForm({ ...form, brideName: e.target.value })}
        />
        <Input
          label="Groom's Church" placeholder="e.g. CSI Christ Church"
          value={form.groomChurch} onChange={(e) => setForm({ ...form, groomChurch: e.target.value })}
        />
        <Input
          label="Bride's Church" placeholder="e.g. CSI St. Peter's"
          value={form.brideChurch} onChange={(e) => setForm({ ...form, brideChurch: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Publication of Banns" />
      <FormLayout.Grid columns={3}>
        <Input
          label="First Publication" type="date"
          value={form.firstDate} onChange={(e) => setForm({ ...form, firstDate: e.target.value })}
        />
        {/* The picker itself refuses a date before the previous publication (CHM-041);
            the save check remains for a value typed by hand. */}
        <Input
          label="Second Publication" type="date" min={form.firstDate || undefined}
          value={form.secondDate} onChange={(e) => setForm({ ...form, secondDate: e.target.value })}
        />
        <Input
          label="Third Publication" type="date" min={form.secondDate || form.firstDate || undefined}
          value={form.thirdDate} onChange={(e) => setForm({ ...form, thirdDate: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Grid>
        <Input
          label="Officiating Presbyter" placeholder="e.g. Rev. David Thomas"
          value={form.officiatingPresbyter} onChange={(e) => setForm({ ...form, officiatingPresbyter: e.target.value })}
        />
        <Input
          label="Objections Raised" type="textarea" rows={2} fullWidth
          placeholder="Leave blank if none were raised during the publication period."
          value={form.objections} onChange={(e) => setForm({ ...form, objections: e.target.value })}
        />
      </FormLayout.Grid>
    </>
  )
};

export { config as bannsConfig };
